import { api } from "./client";
import { parseTags } from "../lib/tags";

export type Status = "backlog" | "todo" | "doing" | "blocked" | "done";

// One ticket shape, end to end. The only deviation from what the backend
// sends on the wire is `tags` (comma-separated string in DB → string[] here),
// because every consumer in the UI works against an array.
export interface Ticket {
  id: number;
  title: string;
  body: string;
  status: Status;
  priority: number;
  estimate: number | null;
  start_date: string | null;
  end_date: string | null;
  parent_id: number | null;
  tags: string[];
  // Derived (read-only) from tags in fromWire. NOT sent back to the backend.
  project: string | null;
  actor: string | null;
  labels: string[]; // tags minus proj:/actor:
  created_at: string;
  updated_at: string;
}

type TicketWire = Omit<Ticket, "tags" | "project" | "actor" | "labels"> & { tags: string };

function fromWire(w: TicketWire): Ticket {
  const tags = w.tags
    ? w.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];
  const { project, actor, labels } = parseTags(tags);
  return { ...w, tags, project, actor, labels };
}

function toWire(t: Partial<Ticket>): Record<string, unknown> {
  const rest: Record<string, unknown> = { ...t };
  // Derived fields are display-only; never POST them back.
  delete rest.project;
  delete rest.actor;
  delete rest.labels;
  const tags = rest.tags as string[] | undefined;
  if (tags === undefined) return rest;
  return { ...rest, tags: tags.join(",") };
}

export interface ListFilters {
  status?: Status;
  parent_id?: number;
  tag?: string;
}

export async function listTickets(
  filters: ListFilters = {},
): Promise<Ticket[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.parent_id !== undefined) {
    params.set("parent_id", String(filters.parent_id));
  }
  if (filters.tag) params.set("tag", filters.tag);
  const qs = params.toString();
  const path = qs ? `/api/tickets?${qs}` : "/api/tickets";
  const wires = await api<TicketWire[]>(path);
  return wires.map(fromWire);
}

export async function getTicket(id: number): Promise<Ticket> {
  return fromWire(await api<TicketWire>(`/api/tickets/${id}`));
}

export interface NewTicket {
  title: string;
  body?: string;
  status?: Status;
  priority?: number;
  estimate?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  parent_id?: number | null;
  tags?: string[];
}

export async function createTicket(t: NewTicket): Promise<Ticket> {
  const wire = await api<TicketWire>("/api/tickets", {
    method: "POST",
    body: JSON.stringify(toWire(t as Partial<Ticket>)),
  });
  return fromWire(wire);
}

export async function updateTicket(
  id: number,
  patch: Partial<Ticket>,
): Promise<Ticket> {
  const wire = await api<TicketWire>(`/api/tickets/${id}`, {
    method: "PATCH",
    body: JSON.stringify(toWire(patch)),
  });
  return fromWire(wire);
}

export async function deleteTicket(id: number): Promise<void> {
  await api(`/api/tickets/${id}`, { method: "DELETE" });
}

export interface TicketTreeNode extends Ticket {
  children: TicketTreeNode[];
}

type TicketTreeNodeWire = TicketWire & { children: TicketTreeNodeWire[] };

function treeFromWire(w: TicketTreeNodeWire): TicketTreeNode {
  return {
    ...fromWire(w),
    children: (w.children ?? []).map(treeFromWire),
  };
}

export async function getTree(parent_id?: number): Promise<TicketTreeNode[]> {
  const path =
    parent_id !== undefined ? `/api/tree?parent_id=${parent_id}` : "/api/tree";
  const wires = await api<TicketTreeNodeWire[]>(path);
  return wires.map(treeFromWire);
}

// Display-only: 1 → "B-001". Never used as URL or storage key.
export function formatId(id: number): string {
  return `B-${String(id).padStart(3, "0")}`;
}
